<template>
    <base-demo-flow
        :phone_key="phone_key"
        :phone_mode="phone_mode"
        :browser_content_key="current_step"
        business_name="Barrel Creek Vineyards"
        phone_number="(555) 123-4567"
        browser_url="barrelcreek.com/account/confirm/xyz789"
        phone_label="Maria's Phone"
        :current_step_number="current_step_number"
        :total_steps="total_steps"
        :progress_percentage="progress_percentage"
        :current_step_name="current_step_name"
        :can_go_previous="can_go_previous"
        :can_go_next="can_go_next"
        :steps="formatted_steps"
        @restart="resetDemo"
        @previous="goToPreviousStep"
        @next="goToNextStep"
        @step-click="goToStep"
    >
        <template #explainer="{ business_name }">
            <component
                :is="explainer_component"
                :customer_data="customer_data"
                :business_name="business_name"
            />
        </template>

        <template #phone-messages>
            <mock-text-conversation :messages="conversation" />
        </template>

        <template #phone-browser>
            <mock-account-confirmation-form
                v-if="current_step === 'confirmation-form'"
                :customer_data="customer_data"
                @submitted="handleConfirmationSubmit"
            />

            <completion-demo
                v-if="current_step === 'complete'"
                :submission_data="submission_data"
            />
        </template>
    </base-demo-flow>
</template>

<script>
import BaseDemoFlow from '@/components/demos/shared/base-demo-flow.vue';
import DemoStepManager from '@/mixins/demo-step-manager.js';
import ExplainerTextMessage from '@/components/demos/crm-account-confirmation/explainer-text-message.vue';
import MockTextConversation from '@/components/demos/shared/mock-text-conversation.vue';

export default {
    name: 'DemoCRMAccountConfirmationFlow',

    mixins: [DemoStepManager],

    components: {
        BaseDemoFlow,
        ExplainerTextMessage,
        MockTextConversation,
        ExplainerAccountConfirmationForm: () => import('@/components/demos/crm-account-confirmation/explainer-account-confirmation-form.vue'),
        ExplainerCompletion: () => import('@/components/demos/crm-account-confirmation/explainer-completion.vue'),
        MockAccountConfirmationForm: () => import('@/components/demos/crm-account-confirmation/mock-account-confirmation-form.vue'),
        CompletionDemo: () => import('@/components/demos/crm-account-confirmation/completion-demo.vue')
    },

    data() {
        return {
            customer_data: {
                customer_id: 'CUST-12345',
                full_name: 'Maria Rodriguez',
                customer_nickname: 'Maria',
                first_name: 'Maria',
                last_name: 'Rodriguez',
                email: 'maria.rodriguez@email.com',
                phone: '(555) 987-6543',
                birth_date: '02/25/1980',
                address: {
                    line1: '789 Vineyard Lane',
                    line2: 'Apt 2B',
                    city: 'Napa',
                    state: 'CA',
                    zip: '94559'
                }
            }
        }
    },

    computed: {
        explainer_component() {
            const component_map = {
                'text-message': 'ExplainerTextMessage',
                'confirmation-form': 'ExplainerAccountConfirmationForm',
                'complete': 'ExplainerCompletion'
            };
            return component_map[this.current_step];
        },

        conversation() {
            return [
                {
                    type: 'customer',
                    text: `Hey ${this.customer_data.customer_nickname}! This is Barrel Creek. We're doing some Spring cleaning and want to make sure we have your latest contact info on file. When you have a moment, please take a second to review and confirm your info. You can reply STOP any time to opt out.`,
                    link: {
                        url: '#',
                        text: 'https://barrelcreek.com/account/confirm/xyz789',
                        preventDefault: true,
                        callback: this.clickTextMessageLink
                    },
                    time: '10:15 AM'
                }
            ];
        }
    },

    methods: {
        getStepConfig() {
            return {
                initial_step: 'text-message',
                initial_phone_mode: 'messages',
                steps: ['text-message', 'confirmation-form', 'complete'],
                step_names: {
                    'text-message': 'SMS Notification',
                    'confirmation-form': 'Information Review',
                    'complete': 'Confirmed',
                },
                step_definitions: {
                    'text-message': {
                        phone_mode: 'messages'
                    },
                    'confirmation-form': {
                        phone_mode: 'browser'
                    },
                    'complete': {
                        phone_mode: 'browser',
                        requires_submission_data: true,
                        default_submission_data: {
                            type: 'confirmation',
                        }
                    },
                }
            };
        },

        clickTextMessageLink() {
            this.navigateToStep('confirmation-form');
        },

        handleConfirmationSubmit(data) {
            this.handleStepSubmission(data, 'complete');
        }
    },

    mounted() {
        document.title = "CRM Account Confirmation Demo | RedChirp";
        this.initializeSteps(this.getStepConfig());
    }
}
</script>

<style lang="scss" scoped>
:deep(.explanation-container) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1.5rem;
    align-items: center;
    padding: 2rem;

    @media (max-width: 900px) {
        padding: .5rem 0;
        gap: 1rem;
    }

    .scenario-context {
        max-width: 500px;
        margin: 0 auto;

        @media (min-width: 900px) {
            margin: 0;
            max-width: none;
        }
    }
}
</style>
